import { useI18n } from '../../../i18n';
// TagSearchInput — search + add tags with + button or Enter
import { useState, useRef, useCallback, useEffect } from 'react';

export interface TagOption {
  id: string;
  label: string;
}

interface TagSearchInputProps {
  label: string;
  tags: TagOption[];
  options: TagOption[];
  isLoading?: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  onAdd: (option: TagOption) => void;
  onRemove: (id: string) => void;
}

export function TagSearchInput({
  label,
  tags,
  options,
  isLoading,
  searchValue,
  onSearchChange,
  onAdd,
  onRemove,
}: TagSearchInputProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = options.filter(
    (o) =>
      !tags.find((t) => t.id === o.id) &&
      o.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleAdd = useCallback(
    (opt: TagOption) => {
      onAdd(opt);
      onSearchChange('');
      inputRef.current?.focus();
    },
    [onAdd, onSearchChange]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) handleAdd(filtered[0]);
    }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={containerRef}>
      <label className="mb-1 block text-sm text-slate-600">{label}</label>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {tags.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
            >
              {t.label}
              <button
                type="button"
                onClick={() => onRemove(t.id)}
                className="ml-0.5 text-blue-400 hover:text-blue-700"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input row */}
      <div className="relative">
        <input
          ref={inputRef}
          value={searchValue}
          onChange={(e) => { onSearchChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('searchToAdd')}
          className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
        />

        {/* Dropdown */}
        {open && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
            {isLoading ? (
              <div className="px-3 py-2 text-xs text-slate-400">{t('loading')}</div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">{t('noResults')}</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleAdd(opt)}
                  className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50"
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
